import { Sample } from "./SampleTypes";

export interface LabOrder {
	id: number;
	order_number: string;
	doctor_id: number;
	healthcare_order_id?: number | null;
	sample_type: string;
	test_category: string;
	test_type: string; // Stored as JSON string on backend, parsed to array in frontend
	status: string;
	priority: string;
	ordered_at: string;
	collected_at?: string;
	received_at?: string;
	completed_at?: string;
	result_id?: number | null;
	notes?: string | null;

	// Shipping and tracking fields
	shipping_status?: string;
	tracking_number?: string;
	delivery_vendor?: string;
	shipped_at?: string;
	delivered_at?: string;
	shipping_address?: string;
	shipping_notes?: string;

	order_items?: LabOrderItem[];
}

export interface LabOrderItem {
	id: number;
	order_id: number;
	sample_id?: string | null;
	sample_type: string;
	test_category: string;
	test_type: string; // Stored as JSON string on backend, parsed to array in frontend
	status: string;
	received_at?: string | null;
	notes?: string | null;
	sample?: Sample;
}

// Helper functions to parse/stringify test_type arrays
export const parseTestType = (testType: string | string[] | null | undefined): string[] => {
	if (!testType) return [];
	if (Array.isArray(testType)) return testType;

	try {
		const parsed = JSON.parse(testType);
		return Array.isArray(parsed) ? parsed : [testType];
	} catch {
		return [testType];
	}
};

export const stringifyTestType = (testType: string | string[] | null | undefined): string => {
	if (!testType) return JSON.stringify([]);
	if (typeof testType === 'string') {
		try {
			// Check if already valid JSON
			JSON.parse(testType);
			return testType;
		} catch {
			return JSON.stringify([testType]);
		}
	}
	return JSON.stringify(Array.isArray(testType) ? testType : [testType]);
};

// Updated order statuses to include shipping workflow
export const LabOrderStatusOptions = [
	{ label: "Pending", value: "pending" },
	{ label: "Confirmed", value: "confirmed" },
	{ label: "Order Prepared", value: "order_prepared" },
	{ label: "Shipped to Customer", value: "shipped_to_customer" },
	{ label: "Delivered to Customer", value: "delivered_to_customer" },
	{ label: "Sample Collected", value: "sample_collected" },
	{ label: "Shipped to Lab", value: "shipped_to_lab" },
	{ label: "Received", value: "received" },
	{ label: "Ready For Processing", value: "ready_for_processing" },
	{ label: "Processing", value: "processing" },
	{ label: "Results Ready", value: "results_ready" },
	{ label: "Reviewed", value: "reviewed" },
	{ label: "Completed", value: "completed" },
	{ label: "Cancelled", value: "cancelled" }
];

export const LabOrderShippingStatusOptions = [
	{ label: "Not Shipped", value: "not_shipped" },
	{ label: "Shipped To Customer", value: "shipped_to_customer" },
	{ label: "Delivered To Customer", value: "delivered_to_customer" },
	{ label: "Shipped To Lab", value: "shipped_to_lab" },
	{ label: "Delivered To Lab", value: "delivered_to_lab" },
	{ label: "Delivery Failed", value: "delivery_failed" }
];

export const LabOrderItemStatusOptions = [
	{ label: "Pending", value: "pending" },
	{ label: "In Progress", value: "in_progress" },
	{ label: "Received", value: "received" },
	{ label: "Processing", value: "processing" },
	{ label: "Completed", value: "completed" },
	{ label: "Failed", value: "failed" }
];

export const LabOrderPriorityOptions = [
	{ label: "Low", value: "low" },
	{ label: "Normal", value: "normal" },
	{ label: "High", value: "high" },
	{ label: "Urgent", value: "urgent" }
];

export const DeliveryVendorOptions = [
	{ label: "FedEx", value: "fedex" },
	{ label: "UPS", value: "ups" },
	{ label: "Australia Post", value: "auspost" },
	{ label: "TNT", value: "tnt" },
	{ label: "DHL", value: "dhl" },
	{ label: "Courier", value: "courier" }
];